import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- Token helpers (localStorage) ----
export function saveTokens(access, refresh) {
  if (access) localStorage.setItem("access_token", access);
  if (refresh) localStorage.setItem("refresh_token", refresh);
}
export function getAccessToken() { return localStorage.getItem("access_token"); }
export function getRefreshToken() { return localStorage.getItem("refresh_token"); }
export function clearTokens() { localStorage.removeItem("access_token"); localStorage.removeItem("refresh_token"); }

// Attach Bearer token + device id to every request
let __deviceId = localStorage.getItem('device_id');
if (!__deviceId) {
  __deviceId = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  localStorage.setItem('device_id', __deviceId);
}
axios.interceptors.request.use((config) => {
  config.headers["X-Device-Id"] = localStorage.getItem('device_id') || __deviceId;
  if (config.headers.Authorization) return config;
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh token on 401
let isRefreshing = false;
let refreshQueue = [];

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry && !orig.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => axios(orig));
      }
      orig._retry = true;
      isRefreshing = true;
      try {
        const rt = getRefreshToken();
        if (!rt) throw new Error("No refresh token");
        // Send the REFRESH token as the Authorization header too — the backend reads
        // the header first, and the default (expired) access token there caused
        // refresh to fail with 401 → random sign-outs mid-use
        const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: rt }, { headers: { Authorization: `Bearer ${rt}` } });
        saveTokens(data.access_token, null);
        refreshQueue.forEach(({ resolve }) => resolve());
        refreshQueue = [];
        return axios(orig);
      } catch (refreshErr) {
        refreshQueue.forEach(({ reject }) => reject(error));
        refreshQueue = [];
        // Sign out ONLY when the server explicitly rejects the session (401/403).
        // Transient failures (offline, 429, 5xx, deploy restarts) must never log the user out.
        const st = refreshErr.response?.status;
        if (st === 401 || st === 403 || !getRefreshToken()) {
          clearTokens();
          localStorage.removeItem("cached_user");
          window.dispatchEvent(new Event("auth-expired"));
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Resolve an image URL. Handles:
 * - Relative API paths (/api/images/xxx) → full URL
 * - data: URLs → pass through
 * - Full http(s) URLs → pass through
 */
export function resolveImageUrl(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}
